import React, { useEffect, useState } from "react";
import DialogPopup from "@_components/DialogPopup/DialogPopup";
import Button from "@mui/material/Button";
import { MenuItem } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { caseActions } from "@_actions/case.actions";
import { casesActions } from "@_actions/cases.actions";
import { caseActivityActions } from "@_actions/caseActivity.actions";
import TextfieldInput from "@_components/TextfieldInput/TextfieldInput";
import { catchError, isNull } from "@_utils/helpers";
import { useSnackbar } from "@_hooks/useSnackbar";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import CancellationReasons, {
  CANCELLATION_REASON_OTHER,
  CANCELLATION_REASON_VALID_LICENSE,
} from "@_configs/cancellationReasons.config";
import CurrencyList from "@_configs/CurrencyList.config";
import styles from "./CancelCasePopup.module.css";

const DETAILS_MAX_LENGTH = 500;

// Currencies sorted alphabetically by code for the dropdown (the shared
// CurrencyList config keeps its own order for other screens).
const SortedCurrencyList = [...CurrencyList].sort((a, b) =>
  a.id.localeCompare(b.id),
);

const defaultState = {
  reason: "",
  details: "",
  paid_amount: "",
  currency: "",
};

function CancelCasePopup(props) {
  const { overviewData, caseId, data, onValidLicenseSelected } = props;
  const dispatch = useDispatch();
  const caseReducer = useSelector((state) => state.caseReducer);
  const { snackbarProps, showSnackbarError } = useSnackbar();

  const defaultCurrency = data?.claim_details?.currency || "";

  const [inputs, setInputs] = useState({
    ...defaultState,
    currency: defaultCurrency,
  });
  const [isClicked, setIsClicked] = useState(false);
  const [cancelInProgress, setCancelInProgress] = useState(false);

  const selectedReason = CancellationReasons.find(
    (reason) => reason.id === inputs.reason,
  );
  const requiresPaidAmount = Boolean(selectedReason?.requiresPaidAmount);
  const requiresDetails = inputs.reason === CANCELLATION_REASON_OTHER;
  const isValidLicense = inputs.reason === CANCELLATION_REASON_VALID_LICENSE;

  const isPaidAmountInvalid = () => {
    const amount = Number(inputs.paid_amount);
    return isNull(inputs.paid_amount) || isNaN(amount) || amount <= 0;
  };

  const closePopup = () => {
    setInputs({ ...defaultState, currency: defaultCurrency });
    setIsClicked(false);
    props.close();
  };

  function handleInputChange(e) {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  }

  function actionClicked() {
    setIsClicked(true);

    if (isNull(inputs.reason)) {
      showSnackbarError("Please select a cancellation reason.");
      return;
    }
    // Valid license: no cancel request here. Adding a license in 'Image Uses'
    // moves the case to VALID_LICENSE on the backend.
    if (isValidLicense) {
      closePopup();
      if (onValidLicenseSelected) {
        onValidLicenseSelected();
      }
      return;
    }
    if (requiresDetails && isNull(inputs.details.trim())) {
      showSnackbarError("Please provide additional details.");
      return;
    }
    if (requiresPaidAmount && isPaidAmountInvalid()) {
      showSnackbarError("Please enter a valid paid amount.");
      return;
    }
    if (requiresPaidAmount && isNull(inputs.currency)) {
      showSnackbarError("Please select a currency.");
      return;
    }

    const additionalInfo = { cancellation_reason: inputs.reason };
    if (inputs.details.trim()) {
      additionalInfo.note = inputs.details.trim();
    }
    if (requiresPaidAmount) {
      additionalInfo.paid_amount = Number(inputs.paid_amount);
      additionalInfo.currency = inputs.currency;
    }

    setCancelInProgress(true);
    dispatch(caseActions.updateCaseStateCancel(caseId, additionalInfo));
  }

  useEffect(() => {
    if (!cancelInProgress) return;

    if (caseReducer.status.CASE_UPDATE_STATE_CANCEL === "success") {
      setCancelInProgress(false);
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_STATE_CANCEL"));
      dispatch(casesActions.getCases());
      dispatch(casesActions.getCasesCount());
      dispatch(caseActions.getCaseWithChargeDetails(caseId, overviewData));
      dispatch(caseActivityActions.getCaseActivities(caseId));
      closePopup();
    } else if (catchError(caseReducer.status.CASE_UPDATE_STATE_CANCEL)) {
      setCancelInProgress(false);
      showSnackbarError(caseReducer.status.CASE_UPDATE_STATE_CANCEL);
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_STATE_CANCEL"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelInProgress, caseReducer.status.CASE_UPDATE_STATE_CANCEL]);

  return (
    <DialogPopup title="Cancel Case" open={props.open} onClose={closePopup}>
      <AppSnackbar {...snackbarProps} />
      <div className={styles.content_container}>
        <div className={styles.intro}>
          Select a reason for cancelling this case.
        </div>

        <div className={styles.reason_row}>
          <div className={styles.reason_field}>
            <TextfieldInput
              label="Cancellation reason"
              select
              id="reason"
              name="reason"
              data-testid="cancel-case-reason"
              value={inputs.reason}
              onChange={handleInputChange}
              error={isClicked && isNull(inputs.reason)}
              required
            >
              {CancellationReasons.map((reason) => (
                <MenuItem key={reason.id} value={reason.id}>
                  {reason.label}
                </MenuItem>
              ))}
            </TextfieldInput>
          </div>

          {requiresPaidAmount && (
            <>
              <div className={styles.currency_field}>
                <TextfieldInput
                  label="Currency"
                  select
                  id="currency"
                  name="currency"
                  data-testid="cancel-case-currency"
                  value={inputs.currency}
                  onChange={handleInputChange}
                  error={isClicked && isNull(inputs.currency)}
                  required
                >
                  {SortedCurrencyList.map((currency) => (
                    <MenuItem key={currency.id} value={currency.id}>
                      {currency.id}
                    </MenuItem>
                  ))}
                </TextfieldInput>
              </div>
              <div className={styles.amount_field}>
                <TextfieldInput
                  label="Paid amount"
                  id="paid_amount"
                  name="paid_amount"
                  type="number"
                  data-testid="cancel-case-paid-amount"
                  placeholder="Enter amount"
                  value={inputs.paid_amount}
                  onChange={handleInputChange}
                  error={isClicked && isPaidAmountInvalid()}
                  required
                />
              </div>
            </>
          )}
        </div>

        <div className={styles.details_area}>
          {isValidLicense ? (
            <div
              className={styles.info_text}
              data-testid="cancel-case-valid-license-info"
            >
              You will be redirected to 'Image Uses' to add a license or note.
            </div>
          ) : (
            <>
              <TextfieldInput
                label={`Additional details${requiresDetails ? "" : " (optional)"}`}
                id="details"
                name="details"
                data-testid="cancel-case-details"
                placeholder="Add any additional details about the cancellation..."
                value={inputs.details}
                onChange={handleInputChange}
                multiline
                minRows={3}
                inputProps={{ maxLength: DETAILS_MAX_LENGTH }}
                error={
                  isClicked && requiresDetails && isNull(inputs.details.trim())
                }
                required={requiresDetails}
              />
              <div className={styles.details_footer}>
                {inputs.details.length} / {DETAILS_MAX_LENGTH}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="action_container">
        <Button
          className="btn_cancel"
          data-testid="cancel-case-cancel-btn"
          onClick={closePopup}
        >
          Cancel
        </Button>
        <Button
          className="btn_confirm"
          data-testid="cancel-case-confirm-btn"
          onClick={actionClicked}
          disabled={cancelInProgress}
        >
          Confirm
        </Button>
      </div>
    </DialogPopup>
  );
}

export default CancelCasePopup;
